/**
 * WhatsApp chat-export ZIP parser. User exports a chat (Settings -> Chats
 * -> Export chat -> Without media), then drops the resulting zip into Dial.
 *
 * We never persist raw messages. Only the derived InteractionStat:
 *   per-month message/call counts in/out, gift-mentions, longest gap.
 *
 * The exporter file format is text:
 *   [DD/MM/YY, HH:MM:SS] Sender Name: message body
 * with system lines for calls (e.g. "Voice call, 7:21")
 */
import type { InteractionStat, Provenance } from './types.ts';
import { MOCK_INTERACTIONS } from './mock-data.ts';

const LINE_RE = /^\[(\d{1,2})\/(\d{1,2})\/(\d{2,4}),\s*(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm|AM|PM)?\]\s*([^:]+):\s*(.+)$/;
const GIFT_KEYWORDS = /\b(gift|present|tohfa|tohfaa|surprise|chocolate|cake|flower|bouquet|hamper)\b/i;
const ME_SENDER_PROXY = '__me__';

export interface WaParseOpts {
  readonly mode: 'mock' | 'file';
  readonly file?: File | Blob;
  readonly me_phone?: string;
  readonly me_aliases?: readonly string[];
}

interface RawLine {
  ts: number;
  sender: string;
  body: string;
  is_system: boolean;
}

export async function parseWaChat(opts: WaParseOpts): Promise<readonly InteractionStat[]> {
  if (opts.mode === 'mock') return MOCK_INTERACTIONS;
  if (!opts.file) throw new Error('file mode requires .file');

  const buf = await opts.file.arrayBuffer();
  const text = await unzipFirstTxt(buf);
  if (!text) return [];
  const lines = parseLines(text);

  const aliases = new Set(['You', 'Aap', 'Aditya', 'Aditya Sharma', ...(opts.me_aliases ?? [])].map((s) => s.toLowerCase()));
  const byContact: Map<string, RawLine[]> = new Map();
  for (const l of lines) {
    if (l.is_system) continue;
    const key = aliases.has(l.sender.toLowerCase()) ? ME_SENDER_PROXY : normaliseContact(l.sender);
    if (!byContact.has(key)) byContact.set(key, []);
    byContact.get(key)!.push(l);
  }
  // If this is a 1:1 chat, the file has 2 senders -- "me" + the partner.
  // If it's a group, multiple senders -- we still group by sender label.
  const partners = [...byContact.keys()].filter((k) => k !== ME_SENDER_PROXY);
  const out: InteractionStat[] = [];
  const now = Date.now();

  for (const partner of partners) {
    const partnerLines = byContact.get(partner)!;
    const mineLines = byContact.get(ME_SENDER_PROXY) ?? [];

    const monthBuckets = new Map<string, { out_msg: number; in_msg: number; out_call: number; in_call: number; gifts: number; last_ts: number; gap: number }>();
    let last_ts: number | null = null;
    let longest_gap_days = 0;
    const allLines = [...partnerLines, ...mineLines].sort((a, b) => a.ts - b.ts);

    for (const l of allLines) {
      const month = isoMonth(l.ts);
      if (!monthBuckets.has(month)) monthBuckets.set(month, { out_msg: 0, in_msg: 0, out_call: 0, in_call: 0, gifts: 0, last_ts: l.ts, gap: 0 });
      const b = monthBuckets.get(month)!;

      const isMine = mineLines.includes(l);
      const isCall = /voice call|video call|missed/i.test(l.body);
      if (isCall && isMine) b.out_call += 1;
      else if (isCall) b.in_call += 1;
      else if (isMine) b.out_msg += 1;
      else b.in_msg += 1;

      if (GIFT_KEYWORDS.test(l.body)) b.gifts += 1;

      if (last_ts != null) {
        const gapDays = Math.floor((l.ts - last_ts) / 86_400_000);
        if (gapDays > longest_gap_days) longest_gap_days = gapDays;
      }
      last_ts = l.ts;
    }

    for (const [month, b] of monthBuckets) {
      const provenance: Provenance = {
        connector: 'wa-chat-export',
        source_id: `${partner}@${month}`,
        fetched_at: now,
      };
      out.push({
        contact_key: partner,
        month,
        messages_out: b.out_msg,
        messages_in: b.in_msg,
        calls_out: b.out_call,
        calls_in: b.in_call,
        gifts_mentioned: b.gifts,
        longest_gap_days,
        provenance,
      });
    }
  }
  return out;
}

function parseLines(text: string): RawLine[] {
  const out: RawLine[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const m = LINE_RE.exec(raw);
    if (!m) continue;
    const [, dd, mm, yyRaw, hh, mi, ampm, sender, body] = m;
    let yy = Number(yyRaw);
    if (yy < 100) yy = yy >= 70 ? 1900 + yy : 2000 + yy;
    let hour = Number(hh);
    if (ampm) {
      if (/pm/i.test(ampm) && hour < 12) hour += 12;
      if (/am/i.test(ampm) && hour === 12) hour = 0;
    }
    const ts = Date.UTC(yy, Number(mm) - 1, Number(dd), hour, Number(mi));
    const senderStr = (sender ?? '').trim();
    const bodyStr = (body ?? '').trim();
    if (!senderStr) continue;
    out.push({ ts, sender: senderStr, body: bodyStr, is_system: false });
  }
  return out;
}

function normaliseContact(s: string): string {
  return s.replace(/‬|‪/g, '').trim();
}
function isoMonth(ts: number): string {
  const d = new Date(ts);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * Minimal ZIP reader -- handles stored + DEFLATE entries to find the first
 * .txt file. Avoids pulling in a full zip library.
 */
async function unzipFirstTxt(buf: ArrayBuffer): Promise<string | null> {
  const view = new DataView(buf);
  const eocdMaxScan = Math.min(buf.byteLength, 65_557);
  let eocdOffset = -1;
  for (let i = buf.byteLength - 22; i >= buf.byteLength - eocdMaxScan; i -= 1) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocdOffset = i;
      break;
    }
  }
  if (eocdOffset < 0) return null;

  const cdSize = view.getUint32(eocdOffset + 12, true);
  const cdOffset = view.getUint32(eocdOffset + 16, true);
  const bytes = new Uint8Array(buf);

  let p = cdOffset;
  while (p < cdOffset + cdSize) {
    if (view.getUint32(p, true) !== 0x02014b50) break;
    const method = view.getUint16(p + 10, true);
    const compSize = view.getUint32(p + 20, true);
    const fnameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const localHeaderOffset = view.getUint32(p + 42, true);
    const filename = new TextDecoder().decode(bytes.subarray(p + 46, p + 46 + fnameLen));
    p += 46 + fnameLen + extraLen + commentLen;

    if (!/\.txt$/i.test(filename)) continue;

    const lhFnameLen = view.getUint16(localHeaderOffset + 26, true);
    const lhExtraLen = view.getUint16(localHeaderOffset + 28, true);
    const dataStart = localHeaderOffset + 30 + lhFnameLen + lhExtraLen;
    const raw = bytes.subarray(dataStart, dataStart + compSize);

    if (method === 0) return new TextDecoder().decode(raw);
    if (method === 8) {
      const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
      return await new Response(stream).text();
    }
    return null;
  }
  return null;
}
